import { useEffect, useMemo, useState } from 'react';
import { Note, Panel, ReportView, Spinner, type ReportDocument, type ReportTable } from '@wyocspay/ui';
import { api, saveBlob, type AuditQuery, type RevenueQuery } from './api.js';

/**
 * Отчетите на платформата.
 *
 * Два документа с различни читатели: приходите, които собственикът дава на
 * счетоводителя си, и одитният дневник, който се вади когато някой пита какво
 * се е случило и кой го е направил.
 *
 * И двата се показват тук с точно същия компонент, с който сървърът ги
 * подготвя за печат, и от същата структура. Преглед, който тихомълком се
 * разминава с изнесения файл, е по-лош от липсата на преглед - защото прегледът
 * е това, което човек проверява, а файлът е това, което изпраща.
 */

// ---------------------------------------------------------------------------

function isoDay(date: Date): string {
  return date.toISOString().slice(0, 10);
}

function monthStart(offset: number): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset, 1));
}

function monthEnd(offset: number): Date {
  const now = new Date();
  return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + offset + 1, 0));
}

const REVENUE_PRESETS: Array<{ key: string; label: string; range: () => { from: string; to: string } }> = [
  {
    key: 'this-month',
    label: 'Този месец',
    range: () => ({ from: isoDay(monthStart(0)), to: isoDay(new Date()) }),
  },
  {
    key: 'last-month',
    label: 'Миналия месец',
    range: () => ({ from: isoDay(monthStart(-1)), to: isoDay(monthEnd(-1)) }),
  },
  {
    key: 'quarter',
    label: 'Това тримесечие',
    range: () => {
      const now = new Date();
      const quarter = Math.floor(now.getUTCMonth() / 3);
      return {
        from: isoDay(new Date(Date.UTC(now.getUTCFullYear(), quarter * 3, 1))),
        to: isoDay(now),
      };
    },
  },
  {
    key: 'year',
    label: 'Тази година',
    range: () => ({
      from: isoDay(new Date(Date.UTC(new Date().getUTCFullYear(), 0, 1))),
      to: isoDay(new Date()),
    }),
  },
];

/**
 * Отваря документа за печат.
 *
 * Прозорецът се отваря преди заявката, защото браузърът позволява нов прозорец
 * само върху жест, който човекът наистина е направил. Ако все пак бъде блокиран,
 * документът се записва като файл - той се отваря и печата от диска по същия
 * начин.
 */
async function printDocument(
  fetchHtml: () => Promise<string>,
  onBlocked: (html: string) => void,
): Promise<void> {
  const target = window.open('', '_blank', 'noopener,noreferrer');
  const html = await fetchHtml();

  if (!target || target.closed) {
    onBlocked(html);
    return;
  }

  target.document.open();
  target.document.write(html);
  target.document.close();
  target.setTimeout(() => {
    target.focus();
    target.print();
  }, 350);
}

// ---------------------------------------------------------------------------

interface ExportHandlers {
  html: () => Promise<string>;
  file: (format: 'csv' | 'xlsx', table?: string) => Promise<Blob>;
}

/** Общата рамка на двата отчета: заглавие, действия и самият документ. */
function DocumentPane({
  document: report,
  handlers,
  onError,
}: {
  document: ReportDocument;
  handlers: ExportHandlers;
  onError: (message: string | null) => void;
}) {
  const [busy, setBusy] = useState<string | null>(null);

  async function withBusy(key: string, work: () => Promise<void>): Promise<void> {
    setBusy(key);
    onError(null);
    try {
      await work();
    } catch (err) {
      onError(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(null);
    }
  }

  return (
    <ReportView
      document={report}
      actions={
        <>
          <button
            type="button"
            className="wc-btn wc-btn-sm"
            disabled={busy !== null}
            onClick={() =>
              void withBusy('print', () =>
                printDocument(handlers.html, (html) => {
                  saveBlob(new Blob([html], { type: 'text/html' }), `${report.slug}.html`);
                  onError(
                    'Браузърът блокира новия прозорец, затова документът е записан като файл. Отворете го и го отпечатайте оттам.',
                  );
                }),
              )
            }
          >
            {busy === 'print' ? 'Отваря се…' : report.words.printButton}
          </button>
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            disabled={busy !== null}
            onClick={() =>
              void withBusy('xlsx', async () => {
                saveBlob(await handlers.file('xlsx'), `${report.slug}.xlsx`);
              })
            }
          >
            {busy === 'xlsx' ? 'Подготвя се…' : 'Excel'}
          </button>
        </>
      }
      tableActions={(table: ReportTable) =>
        table.rows.length === 0 ? null : (
          <button
            type="button"
            className="wc-btn wc-btn-sm wc-btn-secondary"
            disabled={busy !== null}
            onClick={() =>
              void withBusy(`csv-${table.key}`, async () => {
                saveBlob(await handlers.file('csv', table.key), `${report.slug}-${table.key}.csv`);
              })
            }
          >
            {busy === `csv-${table.key}` ? 'Подготвя се…' : 'CSV'}
          </button>
        )
      }
    />
  );
}

// ---------------------------------------------------------------------------
// Приходи
// ---------------------------------------------------------------------------

export function RevenueReportView() {
  const [range, setRange] = useState(() => REVENUE_PRESETS[1].range());
  const [preset, setPreset] = useState('last-month');
  const [report, setReport] = useState<ReportDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const query = useMemo<RevenueQuery>(() => ({ from: range.from, to: range.to }), [range]);

  const days = useMemo(() => {
    const from = Date.parse(`${range.from}T00:00:00Z`);
    const to = Date.parse(`${range.to}T00:00:00Z`);
    if (Number.isNaN(from) || Number.isNaN(to)) return null;
    return Math.round((to - from) / 86_400_000) + 1;
  }, [range]);

  const problem =
    days === null ? 'Въведете две дати.'
    : days <= 0 ? 'Крайната дата е преди началната.'
    : days > 400 ? 'Един отчет обхваща най-много 400 дни. Разделете периода.'
    : null;

  async function generate(): Promise<void> {
    if (problem) return;
    setLoading(true);
    setError(null);
    try {
      setReport(await api.revenueReport(query));
    } catch (err) {
      setReport(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <Note title="Оборотът са чужди пари, приходът на платформата са таксите">
        Оборотът минава през сметките на търговците в Stripe и никога през платформата. Единственото,
        което платформата изкарва, е колоната с таксите. Валутите не се събират: всяка има свой ред и
        свой сбор. Само една таблица прекосява валути - преизчислената в долари - и там курсът и
        източникът му са изписани изрично.
      </Note>

      <Panel title="Период">
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <span className="wc-label" id="revenue-presets">
              Готови периоди
            </span>
            <div
              role="group"
              aria-labelledby="revenue-presets"
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            >
              {REVENUE_PRESETS.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={preset === option.key}
                  className={`wc-btn wc-btn-sm ${preset === option.key ? '' : 'wc-btn-secondary'}`}
                  onClick={() => {
                    setPreset(option.key);
                    setRange(option.range());
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))',
              gap: 14,
            }}
          >
            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">От</span>
              <input
                type="date"
                className="wc-input"
                value={range.from}
                max={range.to}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, from: e.target.value }));
                }}
              />
            </label>
            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">До</span>
              <input
                type="date"
                className="wc-input"
                value={range.to}
                min={range.from}
                onChange={(e) => {
                  setPreset('custom');
                  setRange((r) => ({ ...r, to: e.target.value }));
                }}
              />
            </label>
          </div>

          {problem ? (
            <Note tone="warn" title="Този период не може да бъде отчетен">
              {problem}
            </Note>
          ) : null}

          <div>
            <button
              type="button"
              className="wc-btn"
              disabled={loading || Boolean(problem)}
              onClick={() => void generate()}
            >
              {loading ? 'Подготвя се…' : 'Изготви отчет'}
            </button>
          </div>

          <p style={{ margin: 0, color: 'var(--wc-faint)', fontSize: 12.5, maxWidth: '80ch' }}>
            Реалната такса на Stripe се чете от банковата транзакция на всяко плащане, едно по едно,
            затова дълъг период отнема време. Отчетът казва за колко плащания е успял да я прочете.
          </p>
        </div>
      </Panel>

      {error ? (
        <Note tone="danger" title="Отчетът не може да бъде изготвен">
          {error}
        </Note>
      ) : null}

      {loading ? (
        <Panel>
          <Spinner label="Чете се от базата и от Stripe…" />
        </Panel>
      ) : null}

      {report && !loading ? (
        <DocumentPane
          document={report}
          onError={setError}
          handlers={{
            html: () => api.revenueReportHtml(query),
            file: (format, table) => api.revenueReportFile(query, format, table),
          }}
        />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Одитен дневник
// ---------------------------------------------------------------------------

const SCOPES: Array<{ key: 'day' | 'month' | 'year'; label: string }> = [
  { key: 'day', label: 'Ден' },
  { key: 'month', label: 'Месец' },
  { key: 'year', label: 'Година' },
];

export function AuditReportView() {
  const [scope, setScope] = useState<'day' | 'month' | 'year'>('month');
  const [on, setOn] = useState(() => isoDay(new Date()));
  const [action, setAction] = useState('');
  const [actions, setActions] = useState<Array<{ action: string; count: number }>>([]);
  const [report, setReport] = useState<ReportDocument | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void api
      .auditActions()
      .then((result) => {
        if (live) setActions(result.data);
      })
      .catch(() => undefined);
    return () => {
      live = false;
    };
  }, []);

  const query = useMemo<AuditQuery>(
    () => ({ scope, on, ...(action ? { action } : {}) }),
    [scope, on, action],
  );

  async function generate(): Promise<void> {
    setLoading(true);
    setError(null);
    try {
      setReport(await api.auditReport(query));
    } catch (err) {
      setReport(null);
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={{ display: 'grid', gap: 16, gridTemplateColumns: 'minmax(0, 1fr)' }}>
      <Note title="Стойността на този дневник е веригата, не таблицата">
        Всеки запис се обвързва с хеша на предходния, така че променен или изтрит ред се засича.
        Затова проверката се прави преди изготвянето и резултатът ѝ стои на първата страница на
        документа - включително докъде точно е проверена веригата. Ако проверката не мине, документът
        го казва и не представя записите като достоверни.
      </Note>

      <Panel title="Филтър">
        <div style={{ display: 'grid', gap: 16 }}>
          <div>
            <span className="wc-label" id="audit-scope">
              Обхват
            </span>
            <div
              role="group"
              aria-labelledby="audit-scope"
              style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}
            >
              {SCOPES.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  aria-pressed={scope === option.key}
                  className={`wc-btn wc-btn-sm ${scope === option.key ? '' : 'wc-btn-secondary'}`}
                  onClick={() => setScope(option.key)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
              gap: 14,
            }}
          >
            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">
                {scope === 'day' ? 'Дата' : scope === 'month' ? 'Дата в месеца' : 'Дата в годината'}
              </span>
              <input
                type="date"
                className="wc-input"
                value={on}
                onChange={(e) => setOn(e.target.value)}
              />
            </label>

            <label className="wc-field" style={{ marginBottom: 0 }}>
              <span className="wc-label">Действие</span>
              <select
                className="wc-input"
                value={action}
                onChange={(e) => setAction(e.target.value)}
              >
                <option value="">Всички действия</option>
                {/*
                  Групите с точка накрая филтрират по префикс, така че
                  „payment.“ хваща всичко около плащанията наведнъж. Конкретните
                  действия идват от самата база - показва се това, което наистина
                  се среща, а не списък, който изостава.
                */}
                {[...new Set(actions.map((a) => `${a.action.split('.')[0]}.`))].sort().map((group) => (
                  <option key={group} value={group}>
                    {group}* - цялата група
                  </option>
                ))}
                {actions.map((entry) => (
                  <option key={entry.action} value={entry.action}>
                    {entry.action} ({entry.count})
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div>
            <button
              type="button"
              className="wc-btn"
              disabled={loading}
              onClick={() => void generate()}
            >
              {loading ? 'Проверява се веригата…' : 'Изготви извадка'}
            </button>
          </div>
        </div>
      </Panel>

      {error ? (
        <Note tone="danger" title="Извадката не може да бъде изготвена">
          {error}
        </Note>
      ) : null}

      {loading ? (
        <Panel>
          <Spinner label="Преизчисляват се хешовете на веригата…" />
        </Panel>
      ) : null}

      {report && !loading ? (
        <DocumentPane
          document={report}
          onError={setError}
          handlers={{
            html: () => api.auditReportHtml(query),
            file: (format, table) => api.auditReportFile(query, format, table),
          }}
        />
      ) : null}
    </div>
  );
}
